import React, { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useUser } from "@/lib/context/user-context"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "sonner"

export function AuthDialog({
  open,
  onOpenChange,
  defaultTab = "login"
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultTab?: "login" | "register"
}) {
  const { loginWithEmail, user } = useUser()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<"login" | "register">(defaultTab)
  const [loginSuccess, setLoginSuccess] = useState(false)

  // 监控用户状态，如果设置了用户且标记了登录成功，则关闭对话框
  useEffect(() => {
    if (user && loginSuccess) {
      onOpenChange(false)
      setLoginSuccess(false) // 重置状态
    }
  }, [user, loginSuccess, onOpenChange])

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      if (activeTab === "login") {
        // 登录尝试，但不立即关闭对话框
        try {
          await loginWithEmail(email, password)
          // 登录成功后设置标志
          setLoginSuccess(true)
          toast.success("登录成功") // 只有成功时才显示toast
        } catch (error: any) {
          // 登录错误已在loginWithEmail中处理，这里只记录错误
          console.error("登录错误:", error)
          // 不再显示重复的错误消息
        }
      } else {
        // 注册尝试，但不立即关闭对话框
        try {
          await loginWithEmail(email, password, true)
          // 注册成功后提示但不关闭对话框，让用户看到提示
          toast.success("注册成功，请查收邮箱完成验证")
        } catch (error: any) {
          // 注册错误已在loginWithEmail中处理，这里只记录错误
          console.error("注册错误:", error)
          // 不再显示重复的错误消息
        }
      }
    } catch (error: any) {
      // 兜底错误处理，只在未被内部try/catch捕获时执行
      console.error("未处理的认证错误:", error)
      // 不显示重复的错误消息
      
      // 确保不会关闭对话框
      setLoginSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  // const handleGoogleLogin = async () => {
  //   try {
  //     await login(true)
  //   } catch (error) {
  //     toast.error("Google登录失败")
  //     console.error(error)
  //   }
  // }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-background">
        <DialogHeader>
          <DialogTitle>账号{activeTab === "login" ? "登录" : "注册"}</DialogTitle>
          <DialogDescription>
            {activeTab === "login" ? "登录您的账号以继续使用" : "创建新账号开始学习之旅"}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "login" | "register")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">登录</TabsTrigger>
            <TabsTrigger value="register">注册</TabsTrigger>
          </TabsList>
          
          <TabsContent value="login" className="mt-4">
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">邮箱</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">密码</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "正在登录..." : "登录"}
              </Button>
            </form>
          </TabsContent>
          
          <TabsContent value="register" className="mt-4">
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reg-email">邮箱</Label>
                <Input
                  id="reg-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-password">密码</Label>
                <Input
                  id="reg-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500">密码至少需要6个字符</p>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "正在注册..." : "注册"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        {/* <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                或者使用
              </span>
            </div>
          </div>

          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24px" height="24px" className="mr-2">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
              </svg>
              使用Google登录
            </Button>
          </div>
        </div> */}
      </DialogContent>
    </Dialog>
  )
} 